import { Button, Select, Textarea } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useEffect, useState } from "react";
import PageHeader from "@/components/admin/header/PageHeader";
import { usePrompts } from "@/hooks/usePrompts";

type FormInputs = {
  targetPrompt: string;
  content: string;
};

const PromptEditPage: React.FC = () => {
  const { prompts, getPrompt, updatePrompt, mutate } = usePrompts();
  const [isUpdating, setIsUpdating] = useState(false);

  const form = useForm<FormInputs>({
    initialValues: {
      targetPrompt: "",
      content: "",
    },
    validate: {
      content: (value) => {
        const prompt = getPrompt(form.values.targetPrompt);
        if (!prompt) return null;

        return prompt.requiredVariables.every((v) => value.includes(`{${v}}`))
          ? null
          : `プロンプトには {${prompt.requiredVariables.join("}, {")}} が必要です。`;
      },
    },
  });

  useEffect(() => {
    if (prompts && prompts.length > 0) {
      form.setFieldValue("targetPrompt", prompts[0].id);
      form.setFieldValue("content", prompts[0].content);
    }
  }, [prompts?.length]);

  const options = prompts?.map(p => ({ label: p.name, value: p.id })) ?? [];

  const handleClickSubmit = async () => {
    const result = form.validate();
    if (result.hasErrors) return;

    setIsUpdating(true);
    await updatePrompt(form.values.targetPrompt, form.values.content);
    await mutate();
    setIsUpdating(false);
  };

  return (
    <>
      <PageHeader hasBack={false} title="プロンプト編集" />

      {prompts && (
        <form>
          <Select
            data-testid="prompt-select"
            label="プロンプト選択"
            allowDeselect={false}
            maxDropdownHeight={200}
            style={{ width: "20rem" }}
            data={options}
            value={`${form.values.targetPrompt}`}
            onChange={(value) => {
              form.setFieldValue("targetPrompt", value ?? "");
              form.setFieldValue("content", getPrompt(value ?? undefined)?.content ?? "");
            }}
          />

          <Textarea
            data-testid="prompt-content"
            className="mt-4"
            label="プロンプト"
            autosize
            minRows={20}
            key={form.key("content")}
            value={form.values.content}
            onChange={(event) => {
              form.setFieldValue("content", event.currentTarget.value);
            }}
            error={form.errors.content}
          />

          <div className="flex mt-4 gap-2">
            <Button
              data-testid="submit-button"
              disabled={isUpdating}
              onClick={handleClickSubmit}
            >
              更新する
            </Button>
            <Button
              data-testid="revert-button"
              variant="outline"
              onClick={() => {
                form.setFieldValue("content", getPrompt(form.values.targetPrompt)?.content ?? "");
              }}
            >
              元に戻す
            </Button>
          </div>
        </form>
      )}
    </>
  );
};

export default PromptEditPage;
